import GetProductRequest from '@/modules/common/common/models/DXP/requests/GetProductRequest'
import ProductId from '@/modules/common/common/models/DXP/ProductId'

import PetsInsurance from '@/modules/pets/common/models/APP/PetsInsurance'
import type DXPProduct from '@/modules/common/common/models/DXP/Product'
import SectionFactory from '@/modules/common/common/models/APP/InsuranceSectionsFactory'
import type AbstractAccordion from '@/modules/common/common/models/APP/accordions/AbstractAccordion'
import CommonMapper from '@/modules/core/mappers/CommonMapper'
import type RaterPricing from '@/modules/common/common/models/MID/RaterPricing'

function _getAccordionElements(key: string, items: any[]): AbstractAccordion<any>[] {
  return (
    items?.reduce((acc, attrs: any) => {
      const element = SectionFactory.createAccordion(key, attrs)
      if (!element) return acc

      return [...acc, element]
    }, []) ?? []
  )
}

function _convertSections(
  acc: AbstractAccordion<any>[],
  [key, items]: [string, any[]]
): AbstractAccordion<any>[] {
  if (key === ':type') return acc

  const elements = _getAccordionElements(key, items)
  return [...acc, ...elements]
}

export default class {
  static toRequest(products: ProductId[]): GetProductRequest {
    return new GetProductRequest({ language: 'es', products })
  }

  static fromResponse(DXPResponse: DXPProduct, MIDResponse: Partial<RaterPricing>): PetsInsurance {
    if (!DXPResponse) return new PetsInsurance({})
    const highlights = CommonMapper.fromDXPProductHighlights(DXPResponse.highlights)
    const prices = MIDResponse?.rates ? CommonMapper.fromDXPProductPrices(MIDResponse.rates) : {}

    const sectionEntries = Object.entries(DXPResponse.sections ?? {})
    const sections: AbstractAccordion<any>[] = sectionEntries.reduce(_convertSections, [])
    sections.sort((a, b) => Number(a.order) - Number(b.order))

    const checkItems = ({ text, value: check }: any) => ({ text, check })
    const coverages = SectionFactory.createAccordion('accordionChecks', {
      id: 'coverages',
      items: DXPResponse.coverages?.map(checkItems) ?? []
    })
    const optionalCoverages = SectionFactory.createAccordion('accordionChecks', {
      id: 'optional-coverages',
      items: DXPResponse.optionalCoverages?.map(checkItems) ?? []
    })
    const deficiencies = SectionFactory.createAccordion('accordionChecks', {
      id: 'deficiencies',
      items: DXPResponse.deficiencies?.map(checkItems) ?? []
    })

    return new PetsInsurance({
      quotationNumber: String(MIDResponse?.quotationNumber ?? ''),
      clientCode: DXPResponse.clientCode,
      productCode: DXPResponse.productId,
      productName: DXPResponse?.productName,
      businessLine: MIDResponse?.businessLine,
      image: import.meta.env.VITE_API_URL_DXP_ROOT + DXPResponse?.imagePath,
      highlights,
      coverages,
      optionalCoverages,
      deficiencies,
      sections,
      order: DXPResponse.order,
      group: DXPResponse.family,
      title: DXPResponse.productName,
      description: DXPResponse.productDescription,
      prices
    })
  }
}
